import React, {useState} from 'react'
import styled from 'styled-components'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {useOrganization} from 'organization/OrganizationProvider'
import {useForm} from 'react-hook-form'
import Page from 'lib/ui/layout/Page'
import Typography from '@material-ui/core/Typography'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import {ZoomAreasBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {ErrorText} from 'lib/ui/TextField/HelperText'
import {useHistory} from 'react-router'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import ConfirmUnsavedChangesDialog from '../Configurable/ConfirmUnsavedChangesDialog'

export interface ZoomUserSettings {
  enable_reactions: boolean
  enable_recordings: boolean
  enable_transcripts: boolean
  enable_far_camera_control: boolean
  enable_optimize_recordings: boolean
  enable_join_from_browser: boolean
  enable_recordings_participant_name: boolean
  allow_users_save_chats: boolean
}

export default function UpdateZoomUserSettings() {
  const {register, handleSubmit} = useForm()
  const {event, set} = useEvent()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const saveZoomUserSettings = useSaveZoomUserSettings()
  const history = useHistory()
  const eventRoutes = useEventRoutes()
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  const submit = (data: ZoomUserSettings) => {
    setSubmitting(true)
    setError('')
    saveZoomUserSettings(data)
      .then(() => {
        set({
          ...event,
          zoom_user_enable_reactions: data.enable_reactions,
          zoom_user_enable_recordings: data.enable_recordings,
          zoom_user_enable_transcripts: data.enable_transcripts,
          zoom_user_enable_far_camera_control: data.enable_far_camera_control,
          zoom_user_enable_optimize_recordings: data.enable_optimize_recordings,
          zoom_user_enable_recordings_participant_name:
            data.enable_recordings_participant_name,
          zoom_user_enable_join_from_browser: data.enable_join_from_browser,
          zoom_user_allow_users_save_chats: data.allow_users_save_chats,
        })
        history.push(eventRoutes.areas.root)
      })
      .catch((error) => {
        setError(error.message)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const cancel = () => {
    setShowCancelDialog(true)
  }

  const handleCancelConfirm = () => {
    history.push(eventRoutes.areas.root)
  }

  const handleCancelCancel = () => {
    setShowCancelDialog(false)
  }

  const handleSaveAndClose = () => {
    handleSubmit(submit)()
    setShowCancelDialog(false)
  }

  return (
    <ZoomAreasBreadcrumbs page="Create">
      <Page>
        <Title variant="h5" align="left">
          Zoom User Settings
        </Title>
        <MutedNote>
          These settings will only apply after the room restarts.
        </MutedNote>
        <StyledForm onSubmit={handleSubmit(submit)}>
          <Title variant="h6" align="left">
            Main Settings
          </Title>

          <FieldsHolder>
            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_reactions"
                aria-label="toggle reactions"
                control={
                  <Checkbox defaultChecked={event.zoom_user_enable_reactions} />
                }
                label="Turn on reactions"
              />
              <Info>
                Allow meeting participants to communicate without interrupting
                by reaction with emojis (e.g. clap, heart, laugh). These emojis
                are found in the Reactions menu in the toolbar, and when
                selected, they display on the participant's video and in the
                participants list for 10 seconds. Participants can change their
                emoji skin tone in Settings.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_transcripts"
                aria-label="toggle transcripts"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_transcripts}
                  />
                }
                label="Attendees can see the transcript"
              />
              <Info>
                Allow viewing of full transcript in the in-meeting side panel.
                The Zoom Cloud recording will still have the transcript
                available.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_far_camera_control"
                aria-label="toggle far end camera controll"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_far_camera_control}
                  />
                }
                label="Far end camera control"
              />
              <Info>
                Allow another user to take control of your camera during a
                meeting. Both users (the one requesting control and the one
                giving control) must have this option turned on.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_join_from_browser"
                aria-label="toggle join from browser link"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_join_from_browser}
                  />
                }
                label='Show a "Join from your browser" link'
              />
              <Info>
                Allow participants to bypass the Zoom application download
                process, and join a meeting directly from their browser. This is
                a workaround for participants who are unable to download,
                install, or run applications. <br />
                <b>
                  Note that the meeting experience from the browser is limited.
                </b>
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="allow_users_save_chats"
                aria-label="toggle allow users save chats"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_allow_users_save_chats}
                  />
                }
                label="Allow attendees to save or copy in-meeting chat"
              />
              <Info>
                When this setting is on, attendees can copy the chat or save it
                by clicking 'Save Chat' in the chat window. If this setting is
                off, attendees will not be able to save or copy the chat
                content.
              </Info>
            </StyledFieldset>
          </FieldsHolder>

          <Title variant="h6" align="left">
            Recordings Settings
          </Title>

          <FieldsHolder>
            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_recordings"
                aria-label="toggle recordings"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_recordings}
                  />
                }
                label="Turn on local recordings"
              />
              <Info>
                Allow hosts and participants to record the meeting to a
                computer. The recording will include video and shared content
                matching the recorder's view options, and an audio-only file.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_optimize_recordings"
                aria-label="toggle optimize recordings"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_optimize_recordings}
                  />
                }
                label="Optimize recording for 3rd party video editor"
              />
              <Info>
                Enable this option to ensure compatibility of the recording file
                created by Zoom with video editing software you can use to
                modify the recording file. This will increase both the file size
                and the time it will take to generate the recording file.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_recordings_participant_name"
                aria-label="toggle participant name display"
                control={
                  <Checkbox
                    defaultChecked={
                      event.zoom_user_enable_recordings_participant_name
                    }
                  />
                }
                label="Display participant name"
              />
              <Info>
                Zoom will embed each participant’s name into the recorded video
                (on their video tile), so that when someone watches the
                recording later, they can see the name of who is speaking.
              </Info>
            </StyledFieldset>
          </FieldsHolder>
          <Error>{error}</Error>
          <ButtonRow>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              type="submit"
              aria-label="save zoom user settings"
            >
              Save Settings
            </Button>
            <DangerButton
              variant="contained"
              disabled={submitting}
              aria-label="discard updates made not yet saved"
              onClick={cancel}
            >
              Discard
            </DangerButton>
          </ButtonRow>
        </StyledForm>
        <ConfirmUnsavedChangesDialog
          open={showCancelDialog}
          onDiscard={handleCancelConfirm}
          onCancel={handleCancelCancel}
          onSubmit={handleSaveAndClose}
          expandable={false}
        />
      </Page>
    </ZoomAreasBreadcrumbs>
  )
}

function Error(props: {children: string | null}) {
  if (!props.children) {
    return null
  }

  return (
    <ErrorText color="error" style={{textAlign: 'center', display: 'block'}}>
      We could save your settings: {props.children}
    </ErrorText>
  )
}

function useSaveZoomUserSettings() {
  const {client} = useOrganization()
  const {event} = useEvent()
  const url = api(`/events/${event.id}/zoom_user_settings`)
  return (data: ZoomUserSettings) => client.put<ZoomUserSettings>(url, data)
}

const Info = styled.div`
  opacity: 0.7;
  font-size: 0.9rem;
  margin-top: 4px;
  margin-left: 32px;
  font-style: italic;
`

const Title = styled(Typography)`
  margin-bottom: 12px;
`

const StyledForm = styled.form`
  padding-bottom: 20px;

  & > div {
    width: 100%;
  }
`

const MutedNote = styled.i`
  display: block;
  text-align: left;
  opacity: 0.7;
  font-size: 0.9rem;
  margin-bottom: 32px;
`

const FieldsHolder = styled.div`
  margin-bottom: ${(props) => `${props.theme.spacing[4]}`};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }

  & > div {
    width: 100%;
  }
`

const StyledFieldset = styled(FormControl)`
  width: 100%;
  border-radius: 8px;
  padding: 0 20px;
  cursor: pointer;
  padding-bottom: 10px;

  justify-content: top;

  &:hover {
    background: #f5f5f5;
  }
`

const ButtonRow = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: left;
  gap: 15px;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    justify-content: center;
  }
`

const DangerButton = styled(Button)`
  background-color: #d32f2f;
  color: white;

  &:hover {
    background-color: #b71c1c;
  }
`
